import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  UserIcon,
  BriefcaseIcon,
  HeartIcon,
  BellIcon,
  MagnifyingGlassIcon,
  ChatBubbleLeftRightIcon,
  ArrowRightIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  WalletIcon,
  StarIcon,
  AcademicCapIcon,
  IdentificationIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid, CheckCircleIcon as CheckCircleIconSolid } from '@heroicons/react/24/solid';
import QualificationRegistration from '../components/QualificationRegistration';
import EarningsGoalTracker from '../components/EarningsGoalTracker';
import BannerCarousel from '../components/BannerCarousel';
import { API_BASE } from '../lib/config';

// API base is provided by shared config (CRA/Next compatible)
const API = `${API_BASE}`;

const SeekerDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [applications, setApplications] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [wallet, setWallet] = useState(null);
  const [identityVerification, setIdentityVerification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showQualifications, setShowQualifications] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (user.user_type !== 'job_seeker') {
      navigate('/');
      return;
    }

    fetchDashboardData();
  }, [user, navigate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [applicationsRes, notificationsRes, walletRes, identityRes] = await Promise.all([
        axios.get(`${API}/job-applications`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/notifications`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/wallet`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/identity-verification/status`, { headers }).catch(() => ({ data: null })),
      ]);

      setApplications(applicationsRes.data.slice(0, 3)); // 最新3件
      setNotifications(notificationsRes.data.slice(0, 3)); // 最新3件
      setWallet(walletRes.data);
      setIdentityVerification(identityRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('ログアウトしました');
  };

  const getApplicationStatusIcon = (status) => {
    switch (status) {
      case 'accepted':
        return <CheckCircleIconSolid className="w-5 h-5 text-green-500" />;
      case 'rejected':
        return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
      case 'completed':
        return <CheckCircleIconSolid className="w-5 h-5 text-blue-500" />;
      default:
        return <ClockIcon className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return '応募中';
      case 'accepted': return '採用済';
      case 'rejected': return '不採用';
      case 'completed': return '完了';
      default: return '不明';
    }
  };

  const getIdentityVerificationStatus = () => {
    if (!identityVerification) {
      return {
        status: '未提出',
        statusColor: 'text-gray-600',
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-200',
        message: '本人確認書類をまだ提出していません',
        actionText: '書類を提出',
        actionLink: '/identity-verification',
        showAction: true
      };
    }

    switch (identityVerification.status) {
      case 'in_progress':
        return {
          status: '提出済（確認中）',
          statusColor: 'text-yellow-600',
          bgColor: 'bg-yellow-50',
          borderColor: 'border-yellow-200',
          message: '書類審査中です。1-2営業日お待ちください。',
          showAction: false
        };
      case 'completed':
        return {
          status: '承認済',
          statusColor: 'text-green-600',
          bgColor: 'bg-green-50',
          borderColor: 'border-green-200',
          message: '本人確認が完了し、求人応募が可能です。',
          showAction: false
        };
      case 'rejected':
        return {
          status: '再提出が必要',
          statusColor: 'text-red-600',
          bgColor: 'bg-red-50',
          borderColor: 'border-red-200',
          message: identityVerification.admin_comment || '書類に不備がありました。再提出してください。',
          actionText: '再提出',
          actionLink: '/identity-verification',
          showAction: true
        };
      default:
        return {
          status: '不明',
          statusColor: 'text-gray-600',
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-200',
          message: '本人確認の状態が不明です',
          showAction: false
        };
    }
  };

  if (showQualifications) {
    return (
      <QualificationRegistration onBack={() => setShowQualifications(false)} />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Banner Carousel */}
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pt-6">
        <BannerCarousel />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          <Link
            to="/jobs"
            className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200 hover:border-blue-300"
          >
            <div className="flex flex-col items-center text-center">
              <MagnifyingGlassIcon className="w-8 h-8 text-blue-600 mb-2" />
              <span className="text-sm font-medium text-gray-900 break-words">求人一覧</span>
            </div>
          </Link>

          <Link
            to="/jobs"
            className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200 hover:border-blue-300"
          >
            <div className="flex flex-col items-center text-center">
              <MagnifyingGlassIcon className="w-8 h-8 text-green-600 mb-2" />
              <span className="text-sm font-medium text-gray-900">求人検索</span>
            </div>
          </Link>

          <Link
            to="/mypage"
            className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200 hover:border-blue-300"
          >
            <div className="flex flex-col items-center text-center">
              <UserIcon className="w-8 h-8 text-purple-600 mb-2" />
              <span className="text-sm font-medium text-gray-900">マイページ</span>
            </div>
          </Link>

          <Link
            to="/applications"
            className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200 hover:border-blue-300"
          >
            <div className="flex flex-col items-center text-center">
              <BriefcaseIcon className="w-8 h-8 text-orange-600 mb-2" />
              <span className="text-sm font-medium text-gray-900">応募状況</span>
            </div>
          </Link>

          <Link
            to="/chat"
            className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200 hover:border-blue-300"
          >
            <div className="flex flex-col items-center text-center">
              <ChatBubbleLeftRightIcon className="w-8 h-8 text-indigo-600 mb-2" />
              <span className="text-sm font-medium text-gray-900">メッセージ</span>
            </div>
          </Link>

          <Link
            to="/wallet-management"
            className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200 hover:border-blue-300"
          >
            <div className="flex flex-col items-center text-center">
              <WalletIcon className="w-8 h-8 text-green-600 mb-2" />
              <span className="text-sm font-medium text-gray-900">ウォレット</span>
            </div>
          </Link>

          <button
            onClick={() => setShowQualifications(true)}
            className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-200 hover:border-blue-300"
          >
            <div className="flex flex-col items-center text-center">
              <AcademicCapIcon className="w-8 h-8 text-blue-600 mb-2" />
              <span className="text-sm font-medium text-gray-900">保有資格</span>
            </div>
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Recent Applications */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">最近の応募</h2>
                <Link
                  to="/applications"
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center"
                >
                  すべて見る
                  <ArrowRightIcon className="w-4 h-4 ml-1" />
                </Link>
              </div>

              {applications.length === 0 ? (
                <div className="text-center py-8">
                  <BriefcaseIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">まだ応募していません</p>
                  <Link
                    to="/jobs"
                    className="mt-4 inline-block bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    求人を探す
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {applications.map((app) => (
                    <div key={app.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900">{app.job_title}</h3>
                          <p className="text-sm text-gray-600">{app.employer_name}</p>
                        </div>
                        <div className="flex items-center space-x-2">
                          {getApplicationStatusIcon(app.status)}
                          <span className="text-sm text-gray-600">{getStatusText(app.status)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>


          </div>

          {/* Right Column */}
          <div className="space-y-8">
            {/* Goal Tracker */}
            <EarningsGoalTracker currentEarnings={wallet ? wallet.balance : 0} />

            {/* Identity Verification Status */}
            <div className={`rounded-lg p-6 ${getIdentityVerificationStatus().bgColor} border ${getIdentityVerificationStatus().borderColor}`}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">本人確認</h2>
                <IdentificationIcon className="w-6 h-6 text-gray-600" />
              </div>
              <div className="mb-4">
                <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${getIdentityVerificationStatus().statusColor}`}>
                  {getIdentityVerificationStatus().status === '承認済' ? (
                    <CheckCircleIcon className="w-4 h-4 mr-1" />
                  ) : getIdentityVerificationStatus().status === '提出済（確認中）' ? (
                    <ClockIcon className="w-4 h-4 mr-1" />
                  ) : getIdentityVerificationStatus().status === '再提出が必要' ? (
                    <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                  ) : null}
                  {getIdentityVerificationStatus().status}
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-4">
                {getIdentityVerificationStatus().message}
              </p>
              {getIdentityVerificationStatus().showAction && (
                <Link
                  to={getIdentityVerificationStatus().actionLink}
                  className="inline-block bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  {getIdentityVerificationStatus().actionText}
                </Link>
              )}
            </div>

            {/* Wallet */}
            <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">ウォレット残高</h2>
                <WalletIcon className="w-6 h-6" />
              </div>
              <div className="text-3xl font-bold mb-4">
                {wallet ? wallet.balance.toLocaleString() : '0'}
              </div>
              <Link
                to="/wallet-management"
                className="inline-block bg-white bg-opacity-20 text-white px-4 py-2 rounded-lg hover:bg-opacity-30 transition-colors"
              >
                詳細を見る
              </Link>
            </div>

            {/* Notifications */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">通知</h2>
                <Link
                  to="/mypage?tab=notifications"
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  すべて見る
                </Link>
              </div>

              {notifications.length === 0 ? (
                <div className="text-center py-8">
                  <BellIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">新しい通知はありません</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notifications.map((notif) => (
                    <div key={notif.id} className="border-l-4 border-blue-500 pl-4 py-2">
                      <p className="text-sm font-medium text-gray-900">{notif.title}</p>
                      <p className="text-xs text-gray-600 mt-1">{notif.message}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* User Stats */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">あなたの実績</h2>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">完了した仕事</span>
                  <span className="font-semibold text-gray-900">0件</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">評価</span>
                  <div className="flex items-center">
                    <StarIcon className="w-4 h-4 text-yellow-400 mr-1" />
                    <span className="font-semibold text-gray-900">0.0</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">総収入</span>
                  <span className="font-semibold text-gray-900">0</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SeekerDashboard;